// Thin wrapper around Firebase Analytics — the single chokepoint every GA4 call
// site in the web app passes through. Two rules for callers:
//
// 1. NO PII. Event parameter and user-property values are counts, enums and ids
//    only — never free text, never a person's name or email. A search event
//    carries `result_count`, not the query string. GA4 data is retained and
//    exported downstream; a name that lands there cannot be recalled.
// 2. Nothing here throws or blocks. Every function is a safe no-op when GA4 is
//    not initialized (no REACT_APP_FIREBASE_MEASUREMENT_ID — local dev, tests)
//    and swallows SDK errors, so analytics can never break a UI code path.
// 3. Feature code calls `trackEvent` and nothing else. `setAnalyticsUserContext`
//    belongs to the auth flow — AuthContext, SubscriberSelect,
//    SubscriberSwitcher — and must not be called from a feature component.
//    It sets the WHOLE context (see below), so a reasonable-looking
//    `setAnalyticsUserContext({ subscriberId })` in, say, a download handler
//    would clear the workspace name and role for the rest of the session: the
//    cross-tenant bug this design exists to prevent, introduced by someone
//    trying to be helpful, and passing every test in the suite.
import { logEvent, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase';

// GA4 hard limit: user-property values max 36 characters. Two workspaces whose
// names share a 36-character prefix therefore collapse to the same
// `subscriber_name`. `subscriber_id` still separates them so the underlying
// event data stays correct, but a report grouped on the human-readable name
// would merge those two workspaces into one row.
const MAX_VALUE_LEN = 36;
const truncate = (v) => (typeof v === 'string' ? v.slice(0, MAX_VALUE_LEN) : v);

// A silent no-op is indistinguishable from a working integration — which is
// exactly how GOOGLE_DRIVE_CLIENT_ID (#477) and TYPESENSE_HOST each stayed
// broken for months without leaving a trace. Say it out loud, but only once per
// page load: a per-call warning would bury the console it is trying to inform.
let warnedNotInitialized = false;

function analyticsReady() {
  if (analytics) return true;
  if (!warnedNotInitialized) {
    warnedNotInitialized = true;
    console.warn(
      'analytics: Firebase Analytics is not initialized — no events will be reported. ' +
        'Set REACT_APP_FIREBASE_MEASUREMENT_ID to enable it.'
    );
  }
  return false;
}

export function trackEvent(name, params = {}) {
  if (!analyticsReady()) return;
  try {
    logEvent(analytics, name, params);
  } catch (e) {
    console.warn('analytics: trackEvent failed', e);
  }
}

/**
 * Sets the COMPLETE user context. Anything not supplied is explicitly cleared.
 *
 * GA4 merges user properties rather than replacing them, and only an explicit
 * `null` clears one. Partial updates therefore leak across sessions: logout is
 * an SPA navigation (`MyAppBar` calls `navigate('/login')`, with no page
 * reload), so gtag's property state outlives it. Omitting an absent
 * `subscriberName` would leave the previous tenant's workspace name attached to
 * the next user's events — each one tagged with the correct subscriber_id and
 * the wrong subscriber_name, silently merging two tenants in precisely the
 * per-subscriber report this feature exists to produce. A missing property is
 * recoverable; a confidently wrong one is not.
 *
 * So: callers pass the whole context every time, and absent values clear.
 */
export function setAnalyticsUserContext({ subscriberId, subscriberName, role } = {}) {
  if (!analyticsReady()) return;
  try {
    setUserProperties(analytics, {
      subscriber_id: subscriberId ? truncate(subscriberId) : null,
      subscriber_name: subscriberName ? truncate(subscriberName) : null,
      role: role ? truncate(role) : null,
    });
  } catch (e) {
    console.warn('analytics: setAnalyticsUserContext failed', e);
  }
}
